/**
 * User API Service
 * Handles all user-related API calls using Observables
 */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ApiBaseService, ApiConfig } from './api-base.service';
import { environment } from '../../../environments/environment';
import {
  User,
  UserResponse,
  UserUpdateRequest,
  UpdatePostalCodeRequest,
  UpdateUserLocationRequest,
} from '../models/user.model';
import {
  UserPreferences,
  UserPreferencesHelper,
  UserPreferencesUpdateRequest,
  UserPreferencesResponse,
} from '../models/user-preferences.model';

@Injectable({
  providedIn: 'root',
})
export class UserApiService extends ApiBaseService {
  protected readonly config: ApiConfig = {
    baseUrl: environment.apiUrl,
    version: environment.apiVersion,
  };

  constructor(protected override http: HttpClient) {
    super(http);
  }

  /**
   * Get user profile
   */
  getUserProfile(): Observable<UserResponse> {
    return this.http
      .get<UserResponse>(`${this.baseUrl}/user/profile`)
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * Update user profile
   */
  updateUserProfile(userData: UserUpdateRequest): Observable<UserResponse> {
    return this.http
      .put<UserResponse>(`${this.baseUrl}/user/profile`, userData)
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * Update user postal code
   */
  updateUserPostalCode(location: string): Observable<UserResponse> {
    const request: UpdatePostalCodeRequest = { location };
    return this.http
      .put<UserResponse>(`${this.baseUrl}/user/postalcode`, request)
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * Delete user postal code
   */
  deleteUserPostalCode(): Observable<UserResponse> {
    return this.http
      .delete<UserResponse>(`${this.baseUrl}/user/postalcode`)
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * Update user location with coordinates
   * Used for proximity-based restaurant search
   */
  updateUserLocation(request: UpdateUserLocationRequest): Observable<UserResponse> {
    return this.http
      .put<UserResponse>(`${this.baseUrl}/user/location`, request)
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * Delete user location
   */
  deleteUserLocation(): Observable<UserResponse> {
    return this.http
      .delete<UserResponse>(`${this.baseUrl}/user/location`)
      .pipe(catchError(this.handleError.bind(this)));
  }

  /**
   * Get user preferences
   */
  getUserPreferences(): Observable<UserPreferences> {
    return this.http
      .get<UserPreferences>(`${this.baseUrl}/user/preferences`)
      .pipe(
        map(response => UserPreferencesHelper.fromApiResponse(response)),
        catchError(this.handleError.bind(this))
      );
  }

  /**
   * Update user preferences
   */
  updateUserPreferences(
    preferences: Partial<UserPreferencesUpdateRequest>
  ): Observable<UserPreferencesResponse> {
    return this.http
      .put<UserPreferencesResponse>(`${this.baseUrl}/user/preferences`, preferences)
      .pipe(
        map(response => {
          // Transform the response to include parsed preferences
          return {
            ...response,
            preferences: response.preferences 
              ? UserPreferencesHelper.fromApiResponse(response.preferences)
              : null,
          };
        }),
        catchError(this.handleError.bind(this))
      );
  }
}
